import React, {ReactElement, useMemo} from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import Error from 'next/error';
import styled from 'styled-components';

import MainLayout from 'components/layout/mainLayout';
import PageWrapper from 'components/pageWrapper';
import Icon from 'components/icons/icon';
import ContentWrapper from 'components/contentWrapper';
import Grid from 'components/blocks/blockPartials/grid';
import WhiteBox from 'components/blocks/blockPartials/whiteBox';

import ProductHeader from 'components/pages/product/productHeader';
import ProductMenu from 'components/pages/product/productMenu';
import ProductCard from 'components/blocks/blockPartials/productCard';
import BlockButtonGroup from 'components/blocks/blockButtonGroup';

import getTemplateData from 'utils/getTemplateData';
import storyblokService from 'services/storyblokService.js';

import theme, {bp, color} from 'settings/theme';
import seo from 'utils/seo';

import useBreakpoints from 'hooks/useBreakpoints';

const ProductDetails = dynamic(() => import('components/pages/product/productDetails'), {ssr: true});
const ProductPricing = dynamic(() => import('components/pages/product/productPricing'), {ssr: true});
const ProductSpecs = dynamic(() => import('components/pages/product/productSpecs'), {ssr: true});
const ProductReviews = dynamic(() => import('components/pages/product/productReviews'), {ssr: true});
const ProductDelivery = dynamic(() => import('components/pages/product/productDelivery'), {ssr: true});


type Props = {
  slug: string,
  statusCode: number,
  errorMessage?: string,
  masterTemplateData: any,
  product: any,
  activeTab: string
};

// Product Page
const Page: NextPageExtended<Props> = (props) => {

  const currentBreakpoint: any = useBreakpoints();

  // Get content from supplied StoryBlok story
  const product = useMemo(() => {
    return props.product?.content;
  }, [props.product]);



  // Errors
  if (props.statusCode > 299) {
    return <Error statusCode={props.statusCode} />;
  }

  return (
    <>
    
        <Head>
          <title>{seo.createTitle(product?.seo?.title ?? product?.name)}</title>
          <meta name="description" content={product?.seo?.description ?? product?.summary} />
        </Head>

        <StyledPage className="product">

          <div className="product-footerLayout" itemScope itemType="https://schema.org/IndividualProduct">

            <PageWrapper>

              {/* Header */}
              <ProductHeader
                product={props.product}
              />

              {/* Product Menu */}
              <ProductMenu product={props.product} activeTab={props.activeTab} />

              {/* Details */}
              {props.activeTab === 'details' && 
                <>
                  <ProductDetails product={props.product} />
                  <ProductSpecs product={props.product} />
                  <ProductPricing product={props.product} />
                </>
                  
              }

              {/* Pricing */}
              {props.activeTab === 'pricing' && 
                <>
                  <Head>
                    <link rel="canonical" href={`/products/${props.slug}`} />
                  </Head>
                  <ProductPricing product={props.product} />
                </> 
              }

              {/* Specs */}
              {props.activeTab === 'specs' && 
                <>
                  <Head>
                    <link rel="canonical" href={`/products/${props.slug}`} />
                  </Head>
                  <ProductSpecs product={props.product} />
                </> 
              }

              {/* Reviews */}
              {props.activeTab === 'reviews' && 
                <ProductReviews product={props.product} />
              }

              {props.activeTab === 'delivery' && 
                <ProductDelivery product={props.product} />
              }

              <div style={{height:'3rem'}}></div>

              {/* CTA buttons */}
              <WhiteBox className="product-ctaFooter paddingVerticalAsSpacing">
                <ContentWrapper>
                  
                <BlockButtonGroup
                  block={{
                    display_settings: ['stretchOnMobile', 'centered'],
                    bottom_spacing: 'none',
                    buttons: [
                      {
                        colour: 'green',
                        text: 'CALL TO ORDER',
                        link: {
                          linktype: 'story',
                          cached_url: 'contact/'
                        }
                      },
                      {
                        colour: 'green',
                        text: 'TALK TO AN EXPERT',
                        link: {
                          linktype: 'story',
                          cached_url: 'contact/'
                        }
                      },
                      {
                        colour: 'green',
                        text: 'VISIT OUR SHOWROOM',
                        link: {
                          linktype: 'story',
                          cached_url: 'showroom/'
                        }
                      }
                    ]
                  }}
                />

                </ContentWrapper>
              </WhiteBox>
              

              {/* Related Products */}
              {props.product?.content?.related_products?.length > 0 &&
                <div className="paddingVerticalAsSpacing">

                  <ContentWrapper>
                    <h3 className="spacingBottomHalf">You Might Also Like...</h3>
                  </ContentWrapper>

                  <ContentWrapper fullWidthAt={[1,2]}>

                    <Grid className="spacingBottom">
                      {props.product.content.related_products.map(item => (
                        <ProductCard
                          key={item.uuid}
                          product={item}
                        />
                      ))}
                    </Grid>

                  </ContentWrapper>

                </div>
                  
              }

            </PageWrapper>

            {/* Mobile Buttons */}
            {currentBreakpoint.size < 3 &&
              <div className="product-footerLayout-footer">
                <div className="product-footer">
                  
                  <Link href="/contact" className="product-footer-button">
                    CALL TO ORDER<Icon iconName="chevron" iconDirection="right" />
                  </Link>

                  <Link href="/contact" className="product-footer-button">
                      TALK TO AN EXPERT<Icon iconName="chevron" iconDirection="right" />
                  </Link>

                </div>
              </div>
            }

          </div>

        </StyledPage>

        {storyblokService.bridge()}
        
    </>
  );

}


Page.getLayout = (page: ReactElement) => {
  return (
    <MainLayout masterTemplateData={page.props.masterTemplateData}>
      {page}
    </MainLayout>
  );
};

export default Page;




// Static Props
export async function getServerSideProps({ params, req, res, query, preview = false, previewData = {} }) {

  let statusCode = 200;

  // Create full slug
  let slug = params.slug;
  let getUrl = `cdn/stories/products/${slug}`.toLowerCase();

  // Tabs
  let activeTab = params.tab ? params.tab[0] : undefined;

  if(['details', 'pricing', 'specs', 'reviews', 'delivery'].includes(activeTab) !== true){
    activeTab = 'details';
  }


  // Get Data
  let results;

  try {
    results = await Promise.all([
      getTemplateData(),
      storyblokService.get(getUrl, {resolve_links: 'story', resolve_relations: 'related_products'})
    ]);
  } catch (error) {
    return {
      // revalidate: 1, 
      props: { statusCode: error.response ? error.response.status : 500}
    }
  }

  const masterTemplateData = results[0] ?? null;
  const product = results[1]?.data?.story ?? null;

  // Hack because last items keeps going missing
  masterTemplateData.content.top_menu_categories.push({});

  if(!product){
    statusCode = 404;
  }

  // Return props
  return {
    // revalidate: 1,
    props: { 
      slug,
      statusCode,
      masterTemplateData,
      product,
      activeTab
    }
  };

}


// Styles
const StyledPage = styled.div`

  .product-footerLayout{
    padding-bottom: 40px;
  }

  .product-ctaFooter{
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .product-footerLayout-footer{
    position: fixed;
    bottom: 60px;
    width: 100%;
    height: 40px;
    border-top: solid 1px ${color.lines};
    background-color: white;
  }

  .product-footer{
    display: flex;
    align-items: stretch;
    justify-content: stretch;
    height: 100%;
  }

  .product-footer-button{

    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    padding-left: 5px;
    padding-right: 5px;
    color: ${color.primary};
    font-family: ${theme.typography.fontFamilySecondary};
    font-size: 12px;
    font-weight: bold;
    line-height: 100%;
    white-space: nowrap;
    transition: all 0.15s ease-out;

    &:not(:last-child){
      border-right: solid 1px ${color.lines};
    }

    .icon{

      width: 10px;
      height: 10px;
      margin-left: 5px;

      svg{
        fill: ${color.primary};
      }

    }

    &:hover{
      opacity: 0.5;
    }

  }


  @media (min-width: ${bp(3)}) {
    .product-footerLayout{
      padding-bottom: 0;
    }
  }

  @media (max-width: 374px) {

    .product-footer-hideSmall{
      display: none;
    }

  }


`;



